/* eslint-disable no-magic-numbers -- tuned navigation constants */
import {
  SNAPPING_FORWARD_DISTANCE,
  SNAPPING_HEIGHT_ABOVE_ALIGNMENT,
} from './path-navigation-math';
import type { ScrollNavigationHost } from './alignment-scroll-navigator.types';
import { ScrollNavigationInertia } from './scroll-navigation-inertia';

const CROSS_SECTION_SPEED_DIVISOR = 32;
const MIN_PENDING_DELTA = 0.0001;
/** Past this lag, a large accumulated delta is clamped instead of jumping far along the alignment. */
const NAVIGATION_MAX_LAG_MS = 500;
const MAX_DELTA_AFTER_LAG = 2;
const AUTOPILOT_INTERVAL_MS = 50;
const MAX_AUTOPILOT_SPEED = 800;
/** Below half the snapping distance, navigation slows down proportionally to the zoom. */
const SLOW_DOWN_DISTANCE =
  Math.sqrt(
    SNAPPING_FORWARD_DISTANCE * SNAPPING_FORWARD_DISTANCE +
      SNAPPING_HEIGHT_ABOVE_ALIGNMENT * SNAPPING_HEIGHT_ABOVE_ALIGNMENT
  ) / 2;

/** Moves along the alignment from wheel input, inertia and autopilot. */
export class AlignmentScrollNavigator {
  readonly #inertia = new ScrollNavigationInertia();
  #pendingDelta = 0;
  #isProcessing = false;
  #lastNavigationTime = 0;
  #autoPilotInterval: ReturnType<typeof setInterval> | undefined;
  #autoPilotSpeed = 1;

  constructor(
    private readonly host: ScrollNavigationHost,
    private readonly now: () => number = (): number => performance.now()
  ) {}

  public get inertiaEnabled(): boolean {
    return this.#inertia.enabled;
  }

  public set inertiaEnabled(enabled: boolean) {
    this.#inertia.enabled = enabled;
  }

  public get inertiaMinSpeed(): number {
    return this.#inertia.minSpeed;
  }

  public set inertiaMinSpeed(speed: number) {
    this.#inertia.minSpeed = speed;
  }

  /** @param wheelSpeed web3d scroll speed: negative when scrolling up, which moves forward. */
  public scroll(wheelSpeed: number): void {
    const speed = this.host.isLookingBackwards() ? -wheelSpeed : wheelSpeed;
    void this.#navigate(this.#inertia.registerScroll(speed, this.now()));
  }

  /** Applies the inertia glide; call once per animation frame. */
  public onFrame(): void {
    const delta = this.#inertia.nextInertiaDelta(this.now());
    if (delta !== 0) {
      void this.#navigate(delta);
    }
  }

  public get autoPilot(): boolean {
    return this.#autoPilotInterval !== undefined;
  }

  public set autoPilot(value: boolean) {
    if (value && !this.#autoPilotInterval) {
      this.#autoPilotInterval = setInterval(
        () =>
          void this.#navigate(
            this.#autoPilotSpeed * (this.host.isLookingBackwards() ? 1 : -1)
          ),
        AUTOPILOT_INTERVAL_MS
      );
    } else if (!value && this.#autoPilotInterval) {
      clearInterval(this.#autoPilotInterval);
      this.#autoPilotInterval = undefined;
      this.#pendingDelta = 0;
      this.#inertia.stop();
    }
  }

  /** Percentage of the base speed; values outside ]0, 800] are ignored. */
  public get autoPilotSpeed(): number {
    return this.#autoPilotSpeed * 100;
  }

  public set autoPilotSpeed(speed: number) {
    if (speed > 0 && speed <= MAX_AUTOPILOT_SPEED) {
      this.#autoPilotSpeed = speed / 100;
    }
  }

  /** Motion state is per navigation session. */
  public reset(): void {
    this.autoPilot = false;
    this.#inertia.reset();
    this.#pendingDelta = 0;
  }

  /** Requests made while a move is in flight are coalesced into the next one. */
  async #navigate(delta: number): Promise<void> {
    this.#pendingDelta += this.host.isCrossSectionEnabled()
      ? delta / CROSS_SECTION_SPEED_DIVISOR
      : delta;

    if (this.#isProcessing) {
      return;
    }
    this.#isProcessing = true;

    try {
      while (Math.abs(this.#pendingDelta) > MIN_PENDING_DELTA) {
        const now = this.now();
        let deltaToProcess = this.#pendingDelta;
        this.#pendingDelta = 0;

        if (
          now - this.#lastNavigationTime > NAVIGATION_MAX_LAG_MS &&
          Math.abs(deltaToProcess) > MAX_DELTA_AFTER_LAG
        ) {
          deltaToProcess = Math.sign(deltaToProcess) * MAX_DELTA_AFTER_LAG;
        }

        const cameraDistance = this.host.getCameraDistance();
        if (cameraDistance < SLOW_DOWN_DISTANCE) {
          deltaToProcess *= cameraDistance / SLOW_DOWN_DISTANCE;
        }

        this.#lastNavigationTime = now;
        await this.host.setChainage(
          (this.host.getChainage() ?? 0) - deltaToProcess
        );
      }
    } finally {
      this.#isProcessing = false;
    }
  }
}
